import { useMemo, useState } from 'react';
import axios from 'axios';
import { errorMessage, financeSend, num, won, withCurrent, type FinanceCtx } from '../financeApi';
import MemberPicker from '../common/MemberPicker';
import type { Match } from './importUtils';
import type { FinanceMember } from '../financeApi';

export type ReviewRow = {
  key: number;
  include: boolean;
  recordDate: string;
  rawName: string;
  match: Match;
  candidates: FinanceMember[];
  category: string;
  amount: number;
  memo: string;
  method: string;
  duplicate: boolean;
};

function rowProblem(row: ReviewRow) {
  if (!row.recordDate) return '날짜 없음';
  if (!(row.amount > 0)) return '금액 없음';
  if (!row.category) return '항목 없음';
  return null;
}

// 읽어 들인 헌금 줄을 확인·수정한 뒤 한 번에 저장(POST /finance/bulk). 한 줄이라도 틀리면 서버가 전부 거절한다.
export default function ImportReview({ ctx, initialRows, categories, source, onSaved, onCancel }: {
  ctx: FinanceCtx;
  initialRows: ReviewRow[];
  categories: string[];
  source: 'excel' | 'bank';
  onSaved: (count: number) => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState(initialRows);
  const [searchKey, setSearchKey] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  const update = (key: number, patch: Partial<ReviewRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const stats = useMemo(() => {
    const included = rows.filter((r) => r.include);
    return {
      count: included.length,
      total: included.reduce((s, r) => s + r.amount, 0),
      linked: included.filter((r) => r.match.kind === 'member').length,
      unlinked: included.filter((r) => r.match.kind === 'name').length,
      anonymous: included.filter((r) => r.match.kind === 'anonymous').length,
      guessed: included.filter((r) => r.match.kind === 'member' && r.match.guessed).length,
      problems: included.filter((r) => rowProblem(r)).length,
      duplicates: rows.filter((r) => r.duplicate).length,
    };
  }, [rows]);

  const setAllCategory = (category: string) => setRows((prev) => prev.map((r) => ({ ...r, category })));

  const save = () => {
    const included = rows.filter((r) => r.include);
    if (included.length === 0 || stats.problems > 0) return;
    setSaving(true);
    setError(null);
    setRowErrors({});
    financeSend<{ count: number }>(ctx, 'post', '/bulk', {
      source,
      rows: included.map((r) => ({
        type: '수입',
        recordDate: r.recordDate,
        category: r.category,
        amount: r.amount,
        memberId: r.match.kind === 'member' ? r.match.memberId : null,
        donorName: r.match.kind === 'name' ? r.match.name : '',
        anonymous: r.match.kind === 'anonymous',
        method: r.method,
        memo: r.memo,
      })),
    })
      .then((res) => onSaved(res.count))
      .catch((err) => {
        setError(errorMessage(err, '저장하지 못했습니다.'));
        if (axios.isAxiosError(err) && Array.isArray(err.response?.data?.errors)) {
          const map: Record<number, string> = {};
          err.response?.data.errors.forEach((e: { row: number; message: string }) => {
            const row = included[e.row - 1];
            if (row) map[row.key] = e.message;
          });
          setRowErrors(map);
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <div className="fin-kpis fin-kpis--compact">
        <div className="fin-kpi"><span>저장할 내역</span><strong>{stats.count}건</strong><span className="fin-delta">{won(stats.total)}</span></div>
        <div className="fin-kpi"><span>교적 연결</span><strong>{stats.linked}건</strong><span className="fin-delta">{stats.guessed > 0 ? `추정 ${stats.guessed}건 확인 필요` : '자동 매칭'}</span></div>
        <div className="fin-kpi"><span>이름만 / 무명</span><strong>{stats.unlinked} / {stats.anonymous}건</strong><span className="fin-delta">영수증 합산 제외</span></div>
        <div className="fin-kpi"><span>중복 의심</span><strong>{stats.duplicates}건</strong><span className="fin-delta">기본으로 제외됨</span></div>
      </div>

      <div className="manage-filterbar">
        <span className="fin-muted">항목 일괄 변경</span>
        <select className="fin-select" defaultValue="" onChange={(e) => e.target.value && setAllCategory(e.target.value)}>
          <option value="">선택</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => setRows((p) => p.map((r) => ({ ...r, include: true })))}>전체 선택</button>
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => setRows((p) => p.map((r) => ({ ...r, include: false })))}>전체 해제</button>
      </div>

      <div className="manage-card-block">
        <div className="manage-table-wrap">
          <table className="manage-table fin-review-table">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th style={{ width: 120 }}>날짜</th>
                <th style={{ width: 130 }}>원래 이름</th>
                <th style={{ minWidth: 220 }}>헌금자(교적 연결)</th>
                <th style={{ width: 130 }}>헌금 항목</th>
                <th className="fin-num" style={{ width: 120 }}>금액</th>
                <th style={{ minWidth: 160 }}>메모</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const problem = rowProblem(row) ?? rowErrors[row.key];
                const selectValue = row.match.kind === 'member' ? `m${row.match.memberId}` : row.match.kind;
                return (
                  <tr key={row.key} className={`${row.include ? '' : 'is-excluded'}${problem && row.include ? ' is-problem' : ''}`}>
                    <td><input type="checkbox" checked={row.include} onChange={(e) => update(row.key, { include: e.target.checked })} /></td>
                    <td>
                      <input type="date" className="fin-cell-input" value={row.recordDate} onChange={(e) => update(row.key, { recordDate: e.target.value })} />
                    </td>
                    <td>
                      {row.rawName || <span className="fin-muted">-</span>}
                      {row.duplicate && <div><span className="manage-badge manage-badge--amber">중복 의심</span></div>}
                    </td>
                    <td>
                      {searchKey === row.key ? (
                        <MemberPicker
                          ctx={ctx}
                          value={{ memberId: null, donorName: '' }}
                          autoFocus
                          onChange={(v) => {
                            if (v.memberId) {
                              update(row.key, { match: { kind: 'member', memberId: v.memberId, name: v.donorName, guessed: false } });
                              setSearchKey(null);
                            }
                          }}
                        />
                      ) : (
                        <div className="fin-match">
                          <select
                            className="fin-cell-input"
                            value={selectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'search') return setSearchKey(row.key);
                              if (v === 'anonymous') return update(row.key, { match: { kind: 'anonymous' } });
                              if (v === 'name') return update(row.key, { match: { kind: 'name', name: row.rawName || '이름 없음' } });
                              const member = row.candidates.find((m) => `m${m.id}` === v);
                              if (member) update(row.key, { match: { kind: 'member', memberId: member.id, name: member.name, guessed: false } });
                              return undefined;
                            }}
                          >
                            {row.match.kind === 'member' && !row.candidates.some((m) => row.match.kind === 'member' && m.id === row.match.memberId) && (
                              <option value={selectValue}>{row.match.name}</option>
                            )}
                            {row.candidates.map((m) => (
                              <option key={m.id} value={`m${m.id}`}>
                                {m.name}{m.phone ? ` (${m.phone.slice(-4)})` : ''}{m.department ? ` · ${m.department}` : ''}
                              </option>
                            ))}
                            <option value="name">이름만 기록 (교적 미연결)</option>
                            <option value="anonymous">무명 헌금</option>
                            <option value="search">다른 성도 검색…</option>
                          </select>
                          {row.match.kind === 'member' && row.match.guessed && <span className="manage-badge manage-badge--amber">추정</span>}
                          {row.match.kind === 'member' && !row.match.guessed && <span className="manage-badge manage-badge--green">연결</span>}
                          {row.match.kind === 'name' && row.candidates.length > 1 && <span className="manage-badge manage-badge--amber">동명이인</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <select className="fin-cell-input" value={row.category} onChange={(e) => update(row.key, { category: e.target.value })}>
                        {withCurrent(categories, row.category).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="fin-num">
                      <input className="fin-cell-input fin-num" inputMode="numeric" value={row.amount ? num(row.amount) : ''}
                        onChange={(e) => update(row.key, { amount: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })} />
                    </td>
                    <td>
                      <input className="fin-cell-input" value={row.memo} onChange={(e) => update(row.key, { memo: e.target.value })} />
                      {problem && row.include && <div className="fin-cell-error">{problem}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {error && <div className="fin-inline-error">{error}</div>}
      <div className="fin-actions">
        <button type="button" className="manage-btn" onClick={onCancel}>다시 올리기</button>
        <button type="button" className="manage-btn manage-btn--primary" onClick={save}
          disabled={saving || stats.count === 0 || stats.problems > 0}>
          {saving ? '저장 중...' : stats.problems > 0 ? `확인 필요 ${stats.problems}건` : `${stats.count}건 저장 (${won(stats.total)})`}
        </button>
      </div>
    </>
  );
}
